import { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, Stack, Typography, Box } from '@mui/material';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { areaElementClasses } from '@mui/x-charts/LineChart';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import InfoTooltipIcon from '@/components/icons/InfoTooltipIcon';
import { formatNumber } from '@/utils/formatters';

const capitalizeFirstLetter = (text) =>
  text.charAt(0).toUpperCase() + text.slice(1);

const formatDate = (date) => {
  const day = date.setLocale('da').toFormat('d.');
  const month = capitalizeFirstLetter(date.setLocale('da').toFormat('MMMM'));
  return `${day} ${month}`;
};

// Function to group data by day and sum values
const groupDataByDay = (data) => {
  if (!data || data.length === 0) return [];
  const grouped = data.reduce((acc, { timestamp, value }) => {
    const day = DateTime.fromISO(timestamp).toFormat('yyyy-MM-dd'); // Group by day
    acc[day] = (acc[day] || 0) + value; // Sum values for the same day
    return acc;
  }, {});

  return Object.entries(grouped).map(([date, value]) => ({
    timestamp: date, // Keep the day as the timestamp
    value,
  }));
};

// Function to select 10 representative data points
const selectRepresentativePoints = (data, maxPoints = 20) => {
  if (!data || data.length === 0) return [];

  // Sort by timestamp (grouped by day already)
  const sortedData = [...data].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
  );

  if (sortedData.length <= maxPoints) return sortedData; // If we have <=10 points, use al
  const result = [sortedData[0]]; // Always include first point
  const step = (sortedData.length - 1) / (maxPoints - 1); // Step size for even distribution

  for (let i = 1; i < maxPoints - 1; i++) {
    const index = Math.round(i * step);
    result.push(sortedData[index]);
  }

  result.push(sortedData[sortedData.length - 1]); // Always include last point
  return result;
};

const AreaGradient = ({ color, id }) => (
  <defs>
    <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
      <stop offset="0%" stopColor={color} stopOpacity={0.3} />
      <stop offset="100%" stopColor={color} stopOpacity={0} />
    </linearGradient>
  </defs>
);

const ConsumptionCard = ({ energyDataSummary, dataPoints, warningSlot }) => {
  const { t } = useTranslation('energyxplorer-energy-overview-tab');
  // Group data by day and then select representative points
  const groupedData = useMemo(() => groupDataByDay(dataPoints), [dataPoints]);
  const selectedData = useMemo(
    () => selectRepresentativePoints(groupedData),
    [groupedData],
  );

  const sparklineData = selectedData.map((entry) => entry.value.toFixed(0));
  const tooltipLabels = selectedData.map((entry) =>
    DateTime.fromISO(entry.timestamp)
      .setZone('Europe/Copenhagen')
      .toFormat('dd MMM'),
  );

  const gradientId = 'sparkline-gradient';
  const [selectedRange, setSelectedRange] = useState([
    DateTime.fromISO(energyDataSummary.StartTimestamp).setZone(
      'Europe/Copenhagen',
    ),
    DateTime.fromISO(energyDataSummary.EndTimestamp).setZone(
      'Europe/Copenhagen',
    ),
  ]);

  useEffect(() => {
    setSelectedRange([
      DateTime.fromISO(energyDataSummary.StartTimestamp).setZone(
        'Europe/Copenhagen',
      ),
      DateTime.fromISO(energyDataSummary.EndTimestamp).setZone(
        'Europe/Copenhagen',
      ),
    ]);
  }, [energyDataSummary]);

  const settings = {
    valueFormatter: (value) => `${formatNumber(value)} kWh`,
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack
          direction="column"
          color="inherit"
          elevation={0}
          sx={{
            transition: 'box-shadow 0.3s ease',
            background: 'transparent',
            height: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="body1"
                color="textSecondary"
                fontWeight="bold"
              >
                {t('consumption_card.title')}
              </Typography>
              <InfoTooltipIcon title={t('consumption_card.description')} />
            </Box>
          </Box>
          <Box
            flexDirection={'row'}
            display={'flex'}
            alignContent={'center'}
            gap={2}
          >
            <Typography
              variant="h4"
              color="textPrimary"
              fontWeight="bold"
              gutterBottom
            >
              {formatNumber(energyDataSummary?.TotalEnergyUsage || 0)} kWh
            </Typography>
            {warningSlot}
          </Box>
          {/* <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            I perioden{' '}
            {selectedRange[0] && selectedRange[1]
              ? `${formatDate(selectedRange[0])} - ${formatDate(
                  selectedRange[1],
                )}`
              : 'Vælg periode'}
          </Typography> */}
        </Stack>

        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          I perioden{' '}
          {selectedRange[0] && selectedRange[1]
            ? `${formatDate(selectedRange[0])} - ${formatDate(
                selectedRange[1],
              )}`
            : 'Vælg periode'}
        </Typography>

        <Box>
          <svg width={0} height={0}>
            <defs>
              <linearGradient
                id="sparklineGradientRed"
                x1="0"
                x2="0"
                y1="0"
                y2="1"
              >
                <stop offset="0%" stopColor="#52b202" stopOpacity={0.6} />
                <stop offset="100%" stopColor="#52b202" stopOpacity={0} />
              </linearGradient>
            </defs>
          </svg>
          <SparkLineChart
            data={sparklineData}
            height={50}
            colors={['#98CD00']} // Green color for the line
            showTooltip
            showHighlight
            xAxis={{
              scaleType: 'band', // Required for tooltips to work properly
              data: tooltipLabels, // Provide formatted date labels
            }}
            area={true} // Enables the filled area below the line
            curve="monotoneX" // Smooths out the line curve
            {...settings}
            sx={{
              [`& .${areaElementClasses.root}`]: {
                fill: `url(#${gradientId})`,
              },
            }}
          >
            <AreaGradient color="#52b202" id={gradientId} />
          </SparkLineChart>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ConsumptionCard;
